import {
  Autocomplete,
  Collapse,
  FormControl,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import { useGetPatientCouponsQuery } from "src/api/services/patientsApi";
import {
  useGetOneTimeProductsQuery,
  useGetPricesByProductQuery,
} from "src/api/services/productsApi";
import { useGetPatientTitrationForProductQuery } from "src/api/services/titrationApi";
import { Product, ProductCategories, ProductPrice } from "src/api/types";
import PaymentMethodSelect from "src/shared/PaymentMethodSelect";
import { priceOptionLabel } from "src/utils/priceOptionLabel";

interface Props {
  patientId: number;
  selectedProduct: Product | null;
  setSelectedProduct: (v: Product | null) => void;
  selectedPrice: ProductPrice | null;
  setSelectedPrice: (v: ProductPrice | null) => void;
  quantity?: number;
  setQuantity: (v: number | undefined) => void;
  paymentMethod: string;
  setPaymentMethod: (v: string) => void;
  patientStripeId: string;
  gender: string | null;
}

const BaseCreateOrderForm = ({
  selectedPrice,
  selectedProduct,
  setSelectedPrice,
  setSelectedProduct,
  patientId,
  quantity,
  gender,
  setQuantity,
  paymentMethod,
  setPaymentMethod,
}: Props) => {
  const { data } = useGetOneTimeProductsQuery({
    gender: gender != null ? gender : undefined,
    from: 0,
    to: 100,
    status: "live",
  });
  const { data: coupons = [] } = useGetPatientCouponsQuery({
    id: patientId,
    params: {
      redeemed: false,
    },
  });
  const { data: prices } = useGetPricesByProductQuery(
    selectedProduct
      ? {
          slug: selectedProduct.slug,
          sort_order: JSON.stringify([
            { number_of_months: true },
            { amount: true },
          ]),
          active: true,
        }
      : skipToken,
  );

  const { data: titration } = useGetPatientTitrationForProductQuery(
    selectedProduct && selectedProduct.is_glp1 && patientId
      ? { patient_id: patientId, slug: selectedProduct.slug }
      : skipToken,
  );

  const products = useMemo(() => {
    if (data == null || data.length === 0) return [];

    return data;
  }, [data]);

  const coupon = useMemo(() => {
    if (selectedProduct == null) return 0;
    return coupons
      .filter((c) => c.product_id === selectedProduct.id)
      .reduce((acc, item) => (acc += item.coupon.amount), 0);
  }, [coupons, selectedProduct]);

  const showQuantity = useMemo(() => {
    if (selectedProduct == null) return false;

    return [
      ProductCategories.EmergencyContraception,
      ProductCategories.ErectileDysfunction,
      ProductCategories.Nausea,
    ].includes(selectedProduct.category.name);
  }, [selectedProduct]);

  const filteredPrices = useMemo(() => {
    if (prices == null || prices.length === 0) return [];

    return prices.filter(
      (p) =>
        ![
          "Semaglutide Monthly Injections",
          "Semaglutide Quarterly Injections",
          "Tirzepatide Monthly Injections",
          "Tirzepatide Quarterly Injections",
        ].includes(p.name),
    );
  }, [prices]);

  const count = useMemo(() => {
    return quantity ?? 0;
  }, [quantity]);

  const expectedPrice = useMemo(() => {
    if (selectedPrice == null) return null;

    if (showQuantity) {
      return selectedPrice.product.slug === "ed-ot"
        ? selectedPrice.amount * count * selectedPrice.number_of_months - coupon
        : selectedPrice.amount * count - coupon;
    }

    return selectedPrice.amount - coupon;
  }, [selectedPrice, showQuantity, coupon, count]);

  return (
    <Stack gap={2}>
      <Autocomplete
        options={products}
        getOptionLabel={(p) => p.name}
        value={selectedProduct}
        onChange={(_, newValue) => setSelectedProduct(newValue)}
        renderInput={(params) => (
          <TextField {...params} label="Product" required />
        )}
      />
      <Collapse in={!!selectedProduct} unmountOnExit>
        <Autocomplete
          options={filteredPrices}
          getOptionLabel={(p) =>
            priceOptionLabel(p, titration, selectedProduct)
          }
          value={selectedPrice}
          onChange={(_, newValue) => setSelectedPrice(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Price"
              required
              helperText={
                selectedProduct?.is_glp1
                  ? "⭐ = Patient's next recommended dose"
                  : ""
              }
            />
          )}
        />
      </Collapse>
      {showQuantity ? (
        <Collapse in={!!selectedPrice} unmountOnExit>
          <FormControl fullWidth>
            <TextField
              type="number"
              label="Quantity"
              value={count}
              onChange={(e) => setQuantity(Number(e.target.value))}
            />
          </FormControl>
        </Collapse>
      ) : null}
      <Collapse in={!!selectedPrice} unmountOnExit>
        <Stack gap={2}>
          <Stack>
            {coupon === 0 ? null : (
              <Typography variant="subtitle2">
                Coupon Discount: -${coupon}
              </Typography>
            )}
            {expectedPrice != null ? (
              <Typography variant="subtitle2">
                Expected Patient Price: ${expectedPrice}
              </Typography>
            ) : null}
          </Stack>
          <PaymentMethodSelect
            patientId={patientId}
            selected={paymentMethod}
            setSelected={setPaymentMethod}
          />
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default BaseCreateOrderForm;
